"use client";

import * as React from "react";
import Image from "next/image";
import { Smartphone } from "lucide-react";

// Check if the user agent indicates a mobile device
function isMobileUserAgent(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent.toLowerCase();
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i.test(ua);
}

export function MobileGate({ children }: { children: React.ReactNode }) {
  const [deviceType, setDeviceType] = React.useState<"mobile" | "desktop" | undefined>(undefined);

  React.useEffect(() => {
    // Use user agent to determine device type - this doesn't change on rotation
    const isMobile = isMobileUserAgent();
    setDeviceType(isMobile ? "mobile" : "desktop");
  }, []);

  // Still loading - show nothing to prevent flash
  if (deviceType === undefined) {
    return null;
  }

  // Desktop device - show blocking screen
  if (deviceType === "desktop") {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-[hsl(217,50%,21%)] to-[hsl(223,54%,31%)] px-8 text-center">
        <div className="flex max-w-md flex-col items-center gap-6">
          <div className="flex items-center gap-3">
            <Image
              src="https://assets.fluidityiq.ai/assets/fiq-logo-icon-small.png"
              alt="FluidityIQ"
              width={48}
              height={48}
              className="h-12 w-12"
              priority
            />
            <Image
              src="https://assets.fluidityiq.ai/assets/fiq-logo.png"
              alt="FluidityIQ"
              width={140}
              height={40}
              className="-mb-[10px] -ml-[10px] h-10"
              style={{ width: "auto" }}
              priority
            />
          </div>

          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/10">
            <Smartphone className="h-10 w-10 text-white" />
          </div>

          <div className="space-y-3">
            <h1 className="text-2xl font-bold text-white">
              Mobile Device Required
            </h1>
            <p className="text-base leading-relaxed text-white/80">
              The Patent Scavenger Hunt is designed for mobile devices. Please open this page on your phone to participate.
            </p>
          </div>

          <div className="mt-2 rounded-xl bg-white/10 px-6 py-4">
            <p className="text-sm text-white/70">
              Scan the QR code at the FluidityIQ booth or visit this URL on your mobile device.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Mobile device - show the app (orientation handling is done elsewhere in the app)
  return <>{children}</>;
}
